import type { MessagesPaths as paths, MessagesComponents as components } from '@core/types'
import { createSharedComposable, isAbortError, useCoreConfig, useCoreLogger, useOpenapi, useQuery } from '#imports'
import { matchInGameMessages } from '../utils/matchInGameMessages'
import type { parseNotificationTree } from '../utils/parseNotificationTree'
import type { InGameMessage, InGameMessageWithTree } from '../types/inGameMessages'

export const useInGameMessages = createSharedComposable(() => {
    const client = useOpenapi<paths>({
        baseUrl: '/gateway/message/api',
        service: 'message',
    })

    const { site } = useCoreConfig()
    const logger = useCoreLogger().withTag('useInGameMessages')

    async function withTree(message: InGameMessage, parse: typeof parseNotificationTree): Promise<InGameMessageWithTree | null> {
        const { body, body_html: _bodyHtml, ...rest } = message

        try {
            return { ...rest, tree: await parse(body) }
        }
        catch (error) {
            logger.warn(`In-game message ${message.id} body failed to parse and was dropped`, error)

            return null
        }
    }

    const query = useQuery(async ({ signal }) => {
        let messages: InGameMessage[]
        try {
            const { data } = await client.GET('/v1/public/in-game-messages', {
                signal,
                params: { query: { brand: site.brand as components['schemas']['Brand'] } },
            })

            messages = Array.isArray(data) ? data : []
        }
        catch (error) {
            if (isAbortError(error)) throw error

            return []
        }

        if (!messages.length) return []

        // comark and its parser dependencies are ~100 KB gzip — keep them off every page that renders a GameTile.
        const { parseNotificationTree } = await import('../utils/parseNotificationTree')

        const parsed = await Promise.all(messages.map(message => withTree(message, parseNotificationTree)))

        return parsed.filter((message): message is InGameMessageWithTree => message !== null)
    }, {
        key: 'message:in-game-messages',
        server: false,
        lazy: true,
    })

    function forGame(gameSlug: string, now: number): InGameMessageWithTree[] {
        return matchInGameMessages(query.data.value ?? [], { gameSlug, now })
    }

    return query.enrich({ forGame })
})
